/** Miscellaneous web components not owned by a feature namespace (timezone
 *  select, catalogue browser, entry/mode-select hub, workspaces, player
 *  companion, realtime toasts, and assorted shared widgets). */
export const webMisc = {
  // Shared greeting heading used by the entry screen and the workspaces empty state.
  welcome: "Welcome to OmniMedia",

  // Generic web atoms not present in common (candidates for promotion).
  action: {
    tryAgain: "Try again",
  },

  // Spotify-style catalogue browser (browse by genre/mood/venue or search).
  catalogue: {
    searchPlaceholder: "Search the catalogue…",
    searching: "Searching…",
    noTracks: "No tracks found.",
    add: "Add",
    added: "Added",
    loadMore: "Load more · {count} of {total}",
    dims: {
      genres: "Genres",
      moods: "Moods",
      venues: "Venues",
    },
  },

  // Red "E" explicit-track marker.
  explicit: {
    label: "Explicit",
    tooltip: "Explicit lyrics",
  },

  // App-level error boundary fallback.
  errorFallback: {
    description:
      "An unexpected error occurred. You can try again or refresh the page.",
  },

  // Kiosk exit-PIN dialog.
  exitPinDialog: {
    label: "Exit PIN",
    placeholder: "Enter PIN",
    verifying: "Verifying…",
  },

  // Timezone combobox.
  timezone: {
    selectPlaceholder: "Select timezone…",
    searchPlaceholder: "Search timezones…",
    empty: "No timezone found.",
  },

  // Web-only zod validation messages.
  validation: {
    invalidTimezone: "Invalid IANA timezone",
  },

  // Presigned-URL media upload.
  upload: {
    storageFailed: "Upload to storage failed",
  },

  // Realtime control-channel toasts + reconnecting badge.
  realtime: {
    commandFailed: {
      title: "Command failed",
      fallback: "Device rejected the command",
    },
    deviceNotConnected: {
      title: "Device not connected",
      description:
        "No player is online for this device in the current workspace. Keep the player open in its own tab or window.",
    },
    channelError: {
      title: "Control channel error",
    },
    notConnected: {
      title: "Not connected",
      description:
        "Reconnecting to the workspace control channel. Try again in a moment.",
    },
    commandTimeout: {
      title: "No response from the player",
      description: "It may be reconnecting.",
    },
    syncGroupNotDelivered: {
      title: "Command not delivered",
      description: "No players received the command.",
    },
    reconnecting: "Reconnecting to control channel…",
  },

  // Workspace-session load error screen.
  workspaceLoad: {
    error: "Could not load workspaces",
    sessionExpired: "Your session has expired. Please sign in again.",
    signInAgain: "Sign in again",
    failed: "Failed to load your workspaces.",
  },

  // Post-login hub surface cards (mode-select / hub-surfaces).
  hub: {
    noDevicesYet: "No devices in this workspace yet",
    config: {
      title: "Configuration",
      description:
        "Set up schedules, manage your fleet, team, billing, and workspace settings.",
      noAccess: "No configuration access in this workspace",
    },
    controls: {
      title: "Controls",
      description:
        "Remote-control other players. Pick a screen and run playback controls from this device.",
      noAccess: "No control access in this workspace",
      noDevices: "No devices available to control",
      noOtherScreens: "No other screens to control",
      usePlayerAndControl: "Use Player and Control for this device",
    },
    players: {
      title: "Players",
      description:
        "Turn this device into a locked playback screen (kiosk). Claim a screen to play its assigned schedule.",
    },
    playersAndControls: {
      title: "Player and Control",
      description:
        "Pick a fleet device, open playback in a separate window, and control it from here.",
    },
  },

  // One-click schedule when-spec templates.
  scheduleTemplates: {
    allDay: {
      label: "All day",
      description: "Every day, all day",
      namePrefix: "All day",
    },
    businessHours: {
      label: "Business hours",
      description: "Every day, 09:00–18:00",
      namePrefix: "Business hours",
    },
    weekends: {
      label: "Weekends",
      description: "Saturday & Sunday, all day",
      namePrefix: "Weekends",
    },
    evenings: {
      label: "Evenings",
      description: "Every day, 17:00–23:00",
      namePrefix: "Evenings",
    },
    scheduleName: "{prefix} · {playlistName}",
  },

  // Post-login entry screen (System vs Ad Marketplace).
  entry: {
    prompt: "Where do you want to go?",
    system: {
      title: "System",
      description:
        "Manage your venues, players, schedules, content, team & billing.",
      action: "Open the system",
    },
    marketplace: {
      title: "Ad Marketplace",
      description:
        "Advertise your business in other venues: browse spots, book a flight, track results.",
      action: "Open the marketplace",
    },
  },

  // Per-workspace mode/hub selection page.
  modeSelect: {
    welcomeBack: "Welcome back",
    prompt: "Choose what you want to do in this workspace.",
    errorTitle: "Couldn't load options",
    errorDescription:
      "Something went wrong while loading this workspace's options. Check your connection and try again.",
    loading: "Loading options…",
    button: {
      unavailable: "Unavailable",
      openConfig: "Open configuration",
      openControls: "Open controls",
      setUpPlayer: "Set up player",
      openPlayerAndControl: "Open player and control",
    },
  },

  // 404 page.
  notFound: {
    code: "404",
    title: "Page not found",
    description:
      "This page doesn't exist. The address may be mistyped, or the page may have moved.",
    backHome: "Back to home",
    goToDashboard: "Go to dashboard",
  },

  // Desktop "Players" companion window.
  playerCompanion: {
    thisScreen: "This screen",
    title: "Playing full-screen",
    subtitle: "{name} is playing in the other window.",
    hint: "Keep this window to run the session. The player stays full-screen beside it. Close this window to stop.",
    changeDevice: "Change device",
    backToMenu: "Back to menu",
    nowPlaying: "Now playing",
    nothingPlaying: "Nothing playing",
    playerOffline: "Player offline",
    statusLive: "Live",
    statusConnecting: "Connecting…",
  },

  // Workspaces list + create dialog.
  workspaces: {
    title: "Your workspaces",
    subtitle: "Open an existing workspace or create a new one to get started.",
    emptyDescription:
      "Create your first workspace to start managing devices and content.",
    create: "Create workspace",
    open: "Open workspace",
    memberFallback: "Member",
    dialogDescription:
      "A workspace groups your locations, devices, and team members.",
    nameLabel: "Workspace name",
    namePlaceholder: "e.g. Downtown Stores",
  },

  // Desktop first-run intro (pages/desktop-intro.tsx) — shown once in the
  // Electron shell before the /start hub; never rendered on the web build.
  desktopIntro: {
    tagline: "Venue audio and screens, run from one place.",
    playerTitle: "This computer is a player",
    playerBody: "It plays your music, announcements and signage full-screen.",
    offlineTitle: "It keeps playing offline",
    offlineBody: "Media is cached, so playback continues when the internet drops.",
    controlTitle: "Control lives here too",
    controlBody: "Devices, schedules and live announcements in the same app.",
    getStarted: "Get started",
  },
} as const;
